import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { bool, func, object } from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import api from 'atg/api/API';
import guaranteeOptions from 'atg/constants/GuaranteeOptions';
import { clearQuickOverview, fetchQuickOverview } from 'atg/redux/actions';
import { bet } from 'atg/redux/selectors';
import ReductionReport from 'shared/components/betHeader/ReductionReport';
import ReductionSummary from 'shared/components/betHeader/ReductionSummary';
import { formatTime } from 'shared/utils/dateUtils';
import { getText } from 'shared/utils/Text';

const BaseHorseReductionReports = ({ rows }) =>
  isEmpty(rows) ? null : (
    <>
      {rows.map((row, index) => {
        const { comment, rule, baseHorses = [] } = row;
        return (
          <ReductionReport
            key={index}
            header={rule}
            rows={[
              {
                description: baseHorses
                  .filter(baseHorse => baseHorse[1] !== '-')
                  .map(baseHorse => [
                    <span key={`${baseHorse[0]}_${baseHorse[1]}`}>
                      {baseHorse[0]} {baseHorse[1]}
                    </span>,
                  ]),
              },
            ]}
            optionalLine={comment && <em>{comment}</em>}
          />
        );
      })}
    </>
  );

const JockeyTrainerReport = ({ rows, label }) => {
  // eslint-disable-line react/prop-types
  if (isEmpty(rows)) {
    return null;
  }

  return (
    <>
      {rows.map(row => {
        const { index, minRace, maxRace, description1 } = row;
        const [driverDescriptionInline, ...driverDescriptionRows] =
          description1.split('<br/>');

        let description;
        if (minRace > 0 && maxRace <= 0) {
          description = getText(
            'bet-popup-jockey-trainer-min',
            minRace,
            driverDescriptionInline,
          );
        } else if (minRace <= 0 && maxRace > 0) {
          description = getText(
            'bet-popup-jockey-trainer-max',
            maxRace,
            driverDescriptionInline,
          );
        } else {
          description = getText(
            'bet-popup-jockey-trainer-min-max',
            minRace,
            maxRace,
            driverDescriptionInline,
          );
        }

        if (driverDescriptionRows.length > 0) {
          description = [description, ...driverDescriptionRows];
        }

        return (
          <ReductionReport
            key={index}
            header={`${label} ${index}`}
            rows={[{ description }]}
          />
        );
      })}
    </>
  );
};

const Dividend = ({ v6only, v7only, dividends, onUpdateDividendClick }) => {
  if (isEmpty(dividends)) {
    return null;
  }
  const {
    minDividend,
    maxDividend,
    minV6Dividend,
    maxV6Dividend,
    minV7Dividend,
    maxV7Dividend,
    targetWin,
    nrOfErrors,
    stakeAmountBasedOnTurnoverAt,
  } = dividends;

  let subHeader;
  let rows = [];

  let minMaxDividend;
  if (minDividend && !maxDividend) {
    minMaxDividend = getText('bet-popup-dividend-min', minDividend);
  } else if (!minDividend && maxDividend) {
    minMaxDividend = getText('bet-popup-dividend-max', maxDividend);
  } else if (minDividend && maxDividend) {
    minMaxDividend = getText(
      'bet-popup-dividend-min-max',
      minDividend,
      maxDividend,
    );
  }

  if (minMaxDividend) {
    rows.push(
      `${minMaxDividend}${
        nrOfErrors ? ` ${getText('bet-popup-min-of-corrects', nrOfErrors)}` : ''
      }`,
    );
  }

  if (!v6only) {
    if (minV6Dividend && !maxV6Dividend) {
      rows.push(getText('bet-popup-dividend-v6-min', minV6Dividend));
    } else if (!minV6Dividend && maxV6Dividend) {
      rows.push(getText('bet-popup-dividend-v6-max', maxV6Dividend));
    } else if (minV6Dividend && maxV6Dividend) {
      rows.push(
        getText('bet-popup-dividend-v6-min-max', minV6Dividend, maxV6Dividend),
      );
    }
  }

  if (!v7only) {
    if (minV7Dividend && !maxV7Dividend) {
      rows.push(getText('bet-popup-dividend-v7-min', minV7Dividend));
    } else if (!minV7Dividend && maxV7Dividend) {
      rows.push(getText('bet-popup-dividend-v7-max', maxV7Dividend));
    } else if (minV7Dividend && maxV7Dividend) {
      rows.push(
        getText('bet-popup-dividend-v7-min-max', minV7Dividend, maxV7Dividend),
      );
    }
  }

  if (targetWin) {
    rows.push(getText('bet-popup-dividend-target-win', targetWin));
  }

  rows = rows.map(text => ({ description: text }));

  const latestTurnoverAt = api.gameInformation.getTimestamp();
  if (
    stakeAmountBasedOnTurnoverAt != null &&
    latestTurnoverAt != null &&
    stakeAmountBasedOnTurnoverAt < latestTurnoverAt.getTime()
  ) {
    subHeader = (
      <>
        {getText(
          'bet-popup-dividend-updated-at',
          formatTime(stakeAmountBasedOnTurnoverAt, true),
        )}
        <button type="button" onClick={onUpdateDividendClick}>
          {getText('button-update')}
        </button>
      </>
    );
  }

  return (
    <ReductionReport
      header={getText('bet-popup-dividend-label')}
      subHeader={subHeader}
      rows={rows}
    />
  );
};

const Factor = ({ rows, label }) => {
  if (isEmpty(rows)) {
    return null;
  }

  const reportRows = flatten(
    rows.map(({ factorReduction, legNr }) => {
      const reduced = factorReduction.reduce((acc, factor, index) => {
        if (factor > 1) {
          acc.push({ factor, startNumber: index + 1 });
        }
        return acc;
      }, []);

      return reduced.map(({ factor, startNumber }) => ({
        description: getText('bet-popup-factor', legNr, startNumber, factor),
      }));
    }),
  );

  return <ReductionReport header={label} rows={reportRows} />;
};

const mapStateToProps = state => ({
  isLoading: bet.isQuickOverviewLoading(state),
  quickData: bet.getQuickOverviewData(state),
  error: bet.getQuickOverviewError(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ fetchQuickOverview, clearQuickOverview }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  class ReductionSummaryContainer extends Component {
    static propTypes = {
      quickData: object,
      isLoading: bool.isRequired,
      error: object,
      fetchQuickOverview: func.isRequired,
      invalid: bool,
      visible: bool,
      onUpdate: func.isRequired,
      onUpdateDividendClick: func.isRequired,
    };

    state = {
      shouldFetchWhenVisible: true,
    };

    componentDidMount() {
      this.populateState(this.props);
    }

    componentWillUnmount() {
      this.props.clearQuickOverview();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      this.populateState(nextProps);

      const shouldFetch = this.shouldFetch(nextProps);

      if (!nextProps.visible && shouldFetch) {
        this.setState({
          shouldFetchWhenVisible: true,
        });
      } else if (
        (this.props.visible && shouldFetch) ||
        (nextProps.visible && this.state.shouldFetchWhenVisible)
      ) {
        this.setState({
          shouldFetchWhenVisible: false,
        });
        this.props.fetchQuickOverview();
      }
    }

    componentDidUpdate() {
      this.props.onUpdate();
    }

    shouldFetch(nextProps) {
      const { isLoading, quickData, error, visible, invalid } = nextProps;

      if (isLoading) {
        return false;
      }

      return (
        (quickData == null && error == null) ||
        (quickData == null && visible && !this.props.visible) ||
        (!invalid && this.props.invalid)
      );
    }

    parseReductionReportRows(rows, reportType, indexValue = false) {
      return rows.filter(
        row =>
          row.reportType === reportType &&
          (indexValue === false || row.index === indexValue),
      );
    }

    parseFactorRows(rows, factorReduction) {
      if (!factorReduction || factorReduction.disabled) {
        return [];
      }
      return rows.filter(({ factorReduction = [] }) =>
        factorReduction.some(factor => factor > 1),
      );
    }

    populateState(props) {
      const { quickData = {} } = props;

      const {
        reductionsReport: {
          rows = [],
          genericRules = [],
          renderedGenericRules = [],
        } = {},
        baseHorseReductionReports: baseHorses = [],
        dividends: {
          dividendReduction: { minDividend, maxDividend } = {},
          v6Dividend: {
            minDividend: minV6Dividend,
            maxDividend: maxV6Dividend,
          } = {},
          v7Dividend: {
            minDividend: minV7Dividend,
            maxDividend: maxV7Dividend,
          } = {},
          targetWin: { value: targetWin } = {},
          stakeAmountBasedOnTurnoverAt,
        } = {},
        bet: {
          rows: betRows = [],
          betConfig: {
            guaranteePercent,
            numberOfErrorsGuarantee,
            v6: v6only,
            v7: v7only,
          } = {},
          betKey: { betType } = {},
          reductionRequirements: {
            dividendReduction: { payoutNumberOfErrors = 0 } = {},
            factorReduction,
          } = {},
        } = {},
        faultsAccepted,
        races,
      } = quickData;

      const abcd = this.parseReductionReportRows(rows, 'ABCD');

      // 1. Collect point rows
      // 2. Sort by index field
      // 3. Group by index field
      // 4. Insert matching rows from renderedGenericRules array
      const pointsList = this.parseReductionReportRows(rows, 'POINT')
        .sort((row1, row2) => row1.index - row2.index)
        .reduce((acc, row) => {
          const matchIdx = acc.findIndex(p => p.index === row.index);
          if (matchIdx !== -1) {
            acc[matchIdx].rows.push(row);
          } else {
            acc.push({
              index: row.index,
              rows: [row],
            });
          }
          return acc;
        }, [])
        .map(p => ({
          ...p,
          rows: [
            ...p.rows,
            ...renderedGenericRules.filter(
              ({ reportType, index }) =>
                reportType === 'POINT' && p.index === index,
            ),
          ],
        }));

      const startNumbers = [
        ...this.parseReductionReportRows(rows, 'STARTNUMBER'),
        ...renderedGenericRules.filter(
          ({ reportType }) => reportType === 'STARTNUMBER',
        ),
      ];

      const presetReduction = renderedGenericRules.filter(
        ({ reportType }) => reportType === 'PRESET',
      );

      const drivers = genericRules.filter(
        ({ reportType }) => reportType === 'DRIVER',
      );

      const trainers = genericRules.filter(
        ({ reportType }) => reportType === 'TRAINER',
      );

      const playworthy = this.parseReductionReportRows(rows, 'PLAYWORTHY');

      const dividends = omitBy(
        {
          minDividend,
          maxDividend,
          minV6Dividend,
          maxV6Dividend,
          minV7Dividend,
          maxV7Dividend,
          targetWin,
          nrOfErrors:
            payoutNumberOfErrors > 0
              ? races.length - payoutNumberOfErrors
              : undefined,
          stakeAmountBasedOnTurnoverAt,
        },
        isUndefined,
      );

      const factor = this.parseFactorRows(betRows, factorReduction);

      let systemSettings = [];

      if (quickData.guaranteePercent) {
        systemSettings.push({
          description: quickData.guaranteePercent.replace(/^, /, ''),
        });
      } else if (guaranteePercent && betType) {
        const guaranteeOption = guaranteeOptions[betType];

        if (
          (!guaranteeOption || numberOfErrorsGuarantee === 0) &&
          guaranteePercent < 100 &&
          guaranteePercent > 0
        ) {
          systemSettings.push({
            description: getText(
              'dialog-bet-config-extended-guarantee-percent-value',
              guaranteePercent,
            ),
          });
        } else if (numberOfErrorsGuarantee > 0) {
          // don't show guarantee option if default value is chosen
          systemSettings.push({
            description: getText(
              'dialog-bet-config-extended-guarantee-percent-explain',
              guaranteeOption[numberOfErrorsGuarantee],
            ),
          });
        }
      }

      if (faultsAccepted) {
        systemSettings.push({
          description: `${getText(
            'dialog-bet-config-tolerate-faulting-reductions-label',
          )} ${faultsAccepted} ${getText(
            'dialog-bet-config-tolerate-faulting-reductions-suffix',
          )}`,
        });
      }

      if (v6only) {
        systemSettings.push({
          description: getText('bet-popup-only-betting-v6'),
        });
      }
      if (playworthy) {
        systemSettings = [...systemSettings, ...playworthy];
      }
      if (v7only) {
        systemSettings.push({
          description: getText('bet-popup-only-betting-v7'),
        });
      }

      const trend = genericRules.filter(
        ({ reportType }) => reportType === 'TREND',
      );
      if (trend.length > 0) {
        systemSettings.push({
          description: trend[0].description1,
        });
      }

      this.setState({
        abcd,
        pointsList,
        startNumbers,
        presetReduction,
        baseHorses,
        drivers,
        trainers,
        dividends,
        v6only,
        v7only,
        factor,
        systemSettings,
        isEmpty:
          isEmpty(abcd) &&
          pointsList.every(isEmpty) &&
          isEmpty(startNumbers) &&
          isEmpty(presetReduction) &&
          isEmpty(baseHorses) &&
          isEmpty(drivers) &&
          isEmpty(trainers) &&
          isEmpty(dividends) &&
          isEmpty(factor) &&
          isEmpty(systemSettings),
      });
    }

    render() {
      const { isLoading, onUpdateDividendClick } = this.props;

      const {
        abcd,
        pointsList = [],
        startNumbers,
        presetReduction,
        baseHorses,
        drivers,
        trainers,
        dividends,
        v6only,
        v7only,
        factor,
        systemSettings,
        isEmpty,
      } = this.state;

      return (
        <ReductionSummary isEmpty={isEmpty} isLoading={isLoading}>
          <ReductionReport
            header={getText('bet-popup-abcd-label')}
            rows={abcd}
          />
          {pointsList.map(points => (
            <ReductionReport
              header={getText('bet-popup-points-label', points.index)}
              key={points.index}
              rows={points.rows}
            />
          ))}
          <BaseHorseReductionReports rows={baseHorses} />
          <ReductionReport
            header={getText('bet-popup-startnumber-label')}
            rows={startNumbers}
          />
          <ReductionReport
            header={getText('bet-popup-preset-reduction-label')}
            rows={presetReduction}
          />
          <JockeyTrainerReport
            label={getText('bet-popup-driver-label')}
            rows={drivers}
          />
          <JockeyTrainerReport
            label={getText('bet-popup-trainer-label')}
            rows={trainers}
          />
          <Dividend
            v6only={v6only}
            v7only={v7only}
            dividends={dividends}
            onUpdateDividendClick={onUpdateDividendClick}
          />
          <Factor label={getText('bet-popup-factor-label')} rows={factor} />
          <ReductionReport
            header={getText('bet-popup-system-settings-label')}
            rows={systemSettings}
          />
        </ReductionSummary>
      );
    }
  },
);
